// src/components/modals/upsert-event-v2/UpsertEventDetailsStep.jsx
import { Calendar, Clock3, Users, MapPin, FileText, BookOpenText } from "lucide-react";
import useAuth from "../../../hooks/useAuth";
import EditableImage from "../../EditableImage";
import InfoField from "../../InfoField";
import Toggle from "../../Toggle";
import TextEditor from "../../TextEditor";
import EuropeanDateInput from "../../EuropeanDateInput";

function buildDateTime(dateStr, timeStr) {
  if (!dateStr) return null;
  const time = timeStr || "00:00";
  const [hours, minutes] = time.split(":").map(Number);
  const d = new Date(dateStr);
  if (Number.isFinite(hours) && Number.isFinite(minutes)) {
    d.setHours(hours, minutes, 0, 0);
  }
  return d;
}

export default function UpsertEventDetailsStep({ event, setEvent, step, onNext, onBack }) {
  const { user } = useAuth();
  const startDateTime = buildDateTime(event?.startDate, event?.startTime);
  const endDateTime = buildDateTime(event?.endDate, event?.endTime);
  const now = new Date();

  const isStartInPast = !!startDateTime && startDateTime < now;
  const isEndBeforeStart = !!startDateTime && !!endDateTime && endDateTime < startDateTime;

  // Steps: 0 = Cover/Organizer, 1 = Dates/Times, 2 = Info, 3 = Gallery
  return (
    <>
      {step === 0 && (
        <>
          {/* Cover image */}
          <EditableImage
            base64={true}
            image={event?.coverImage}
            label="Event cover image"
            onImageChange={(fileOrString) => {
              setEvent((prev) => ({ ...prev, coverImage: fileOrString || "" }));
            }}
          />
          {/* Organizer + Paid toggle */}
          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center gap-3">
              <img
                src={`https://api.dicebear.com/9.x/initials/svg?backgroundColor=2B36D9&seed=${encodeURIComponent(
                  user?.name || "User"
                )}`}
                alt={user?.name || "User"}
                className="h-10 w-10 rounded-full object-cover"
              />
              <div>
                <div className="font-semibold text-gray-900">{user?.name || "User"}</div>
                <div className="text-sm">Organizer</div>
              </div>
            </div>
            <div className="flex items-center">
              <Toggle
                checked={!!event?.isPaid}
                onChange={(checked) => setEvent((p) => ({ ...p, isPaid: checked }))}
              />
              <span className="ml-2">Paid event</span>
            </div>
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <div className="grid grid-cols-2 gap-4 mt-6">
            {/* Column 1: Start/End Date */}
            <div className="space-y-4">
              <InfoField icon={<Calendar size={18} />} label="Start Date">
                <EuropeanDateInput
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 bg-white pr-10"
                  value={event?.startDate || ""}
                  onChange={(isoDate) => setEvent((p) => ({ ...p, startDate: isoDate }))}
                />
              </InfoField>
              <InfoField icon={<Calendar size={18} />} label="End Date">
                <EuropeanDateInput
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 bg-white pr-10"
                  value={event?.endDate || ""}
                  onChange={(isoDate) => setEvent((p) => ({ ...p, endDate: isoDate }))}
                />
              </InfoField>
            </div>
            {/* Column 2: Start/End Time */}
            <div className="space-y-4">
              <InfoField icon={<Clock3 size={18} />} label="Start Time">
                <input
                  type="time"
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 bg-white"
                  value={event?.startTime || ""}
                  onChange={(e) => setEvent((p) => ({ ...p, startTime: e.target.value }))}
                />
              </InfoField>
              <InfoField icon={<Clock3 size={18} />} label="End Time">
                <input
                  type="time"
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 bg-white"
                  value={event?.endTime || ""}
                  onChange={(e) => setEvent((p) => ({ ...p, endTime: e.target.value }))}
                />
              </InfoField>
            </div>
            {(isStartInPast || isEndBeforeStart) && (
              <div className="col-span-2 text-sm text-red-600 space-y-1">
                {isStartInPast && (
                  <p>Start date and time cannot be in the past.</p>
                )}
                {isEndBeforeStart && (
                  <p>End date and time must be after start date and time.</p>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className="grid grid-cols-2 gap-4 mt-6">
            {/* Max Attendees */}
            <InfoField icon={<Users size={18} />} label="Max Attendees" className="col-span-2 mt-4">
              <input
                type="number"
                min={1}
                inputMode="numeric"
                placeholder="e.g., 100"
                className="w-full bg-white rounded-lg border border-gray-300 px-3 py-2"
                value={event?.maxAttendees ?? ""}
                onChange={(e) =>
                  setEvent((p) => ({
                    ...p,
                    maxAttendees: e.target.value === "" ? "" : Number(e.target.value),
                  }))
                }
              />
            </InfoField>
            {/* Title */}
            <InfoField icon={<FileText size={18} />} label="Title" className="col-span-2">
              <input
                type="text"
                placeholder="Title"
                className="w-full rounded-xl border border-gray-300 px-3 py-2 bg-white"
                value={event?.name || ""}
                onChange={(e) => setEvent((p) => ({ ...p, name: e.target.value }))}
              />
            </InfoField>
            {/* Location */}
            <InfoField icon={<MapPin size={18} />} label="Location" className="col-span-2">
              <input
                type="text"
                placeholder="Event location"
                className="w-full rounded-xl border border-gray-300 px-3 py-2 bg-white"
                value={event?.location || ""}
                onChange={(e) => setEvent((p) => ({ ...p, location: e.target.value }))}
              />
            </InfoField>
            {/* Description */}
            <InfoField icon={<BookOpenText size={18} />} label="Description" className="col-span-2">
              <div className="w-full rounded-xl border border-gray-300 bg-white overflow-hidden">
                <TextEditor
                  initialContent={event?.description}
                  open={true}
                  setValue={(value) => setEvent((p) => ({ ...p, description: value }))}
                />
              </div>
            </InfoField>
            {/* Program */}
            <InfoField icon={<BookOpenText size={18} />} label="Program" className="col-span-2">
              <div className="w-full rounded-xl border border-gray-300 bg-white overflow-hidden">
                <TextEditor
                  initialContent={event?.program}
                  open={true}
                  setValue={(value) => setEvent((p) => ({ ...p, program: value }))}
                />
              </div>
            </InfoField>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <div className="col-span-3 grid grid-cols-3 gap-4">
            {event.galleryImages.map((img, idx) => (
              <EditableImage
                key={idx}
                className="col-span-1 !h-auto w-full"
                base64={true}
                image={img}
                label={`Gallery image ${idx + 1}`}
                onImageChange={(fileOrString) => {
                  setEvent((prev) => {
                    const arr = [...(prev.galleryImages || [])];
                    if (!fileOrString) {
                      arr.splice(idx, 1);
                    } else {
                      arr[idx] = fileOrString;
                    }
                    return { ...prev, galleryImages: arr };
                  });
                }}
              />
            ))}
            {/* Slot to add new images */}
            <EditableImage
              className="col-span-1 !h-auto w-full"
              base64={true}
              label="Add gallery image"
              onImageChange={(fileOrString) => {
                if (!fileOrString) return;
                setEvent((prev) => ({
                  ...prev,
                  galleryImages: [...(prev.galleryImages || []), fileOrString],
                }));
              }}
            />
          </div>
        </>
      )}

      {/* Botones de navegación eliminados, ahora solo se usan los del modal principal */}
    </>
  );
}